import { fromJS } from "immutable";
import axios from "axios";
import brand from "dan-api/dummy/brand";
import base64 from "base-64";
import cookie from "react-cookies";

// Actions
const SET_DATE_SLOT_VISIBLE = "SET_DATE_SLOT_VISIBLE";
const SET_DATE_SLOT_DATA = "SET_DATE_SLOT_DATA";
const ADD_TIME_SLOT = "ADD_TIME_SLOT";
const REMOVE_TIME_SLOT = "REMOVE_TIME_SLOT";
const UPDATE_SETTINGS = "UPDATE_SETTINGS";
const SAVE_RESTAURANT_SLOTS = "SAVE_RESTAURANT_SLOTS";
const SAVE_SLOTS_LOADING = "SAVE_SLOTS_LOADING";
const SAVE_SLOTS_SUCCESS = "SAVE_SLOTS_SUCCESS";
const SAVE_SLOTS_ERROR = "SAVE_SLOTS_ERROR";
const FETCH_CONFIG_LOADING = "FETCH_CONFIG_LOADING";
const FETCH_CONFIG_SUCCESS = "FETCH_CONFIG_SUCCESS";
const FETCH_CONFIG_ERROR = "FETCH_CONFIG_ERROR";
const CUSTOMER_ERROR = "CUSTOMER_ERROR";
const CLEAR_ERROR = "CLEAR_ERROR";

// Initial State
const initialState = fromJS({
  dateSlotVisible: false,
  dateSlotData: null,
  currentRestaurantData: {
    slots: {},
    settings: {
      useAppointmentSystem: false,
      preStartMinutes: 10,
    },
  },
  loading: false,
  error: null,
});

// Reducer
export default function dateSlotSystemReducer(
  state = initialState,
  action = {}
) {
  switch (action.type) {
    case SET_DATE_SLOT_VISIBLE: {
      const newState = state
        .set("dateSlotVisible", action.visible)
        .set("dateSlotData", action.data ? fromJS(action.data) : null);

      if (action.visible && action.data?.BrandId) {
        const brandId = action.data.BrandId;
        const restaurantData =
          state.getIn(["restaurantSlots", brandId]) ||
          fromJS({
            slots: {},
            settings: {
              useAppointmentSystem: false,
              preStartMinutes: 10,
            },
          });
        return newState.set("currentRestaurantData", restaurantData);
      }
      return newState;
    }

    case SET_DATE_SLOT_DATA:
      return state.set("dateSlotData", fromJS(action.data));

    case ADD_TIME_SLOT: {
      const currentSlots = state.getIn(
        ["currentRestaurantData", "slots", action.day.toString()],
        fromJS([])
      );
      return state.setIn(
        ["currentRestaurantData", "slots", action.day.toString()],
        currentSlots.push(
          fromJS({ startTime: action.startTime, endTime: action.endTime })
        )
      );
    }

    case REMOVE_TIME_SLOT: {
      const daySlots = state.getIn([
        "currentRestaurantData",
        "slots",
        action.day.toString(),
      ]);
      return state.setIn(
        ["currentRestaurantData", "slots", action.day.toString()],
        daySlots.delete(action.slotIndex)
      );
    }

    case UPDATE_SETTINGS:
      return state.mergeIn(
        ["currentRestaurantData", "settings"],
        fromJS(action.settings)
      );

    case SAVE_RESTAURANT_SLOTS: {
      const brandId = action.brandId;
      const currentData = state.get("currentRestaurantData");
      return state.setIn(["restaurantSlots", brandId], currentData);
    }

    case SAVE_SLOTS_LOADING:
      return state.set("loading", action.loading);

    case SAVE_SLOTS_SUCCESS:
      return state.merge({
        loading: false,
        error: null,
      });

    case SAVE_SLOTS_ERROR:
      return state.merge({
        loading: false,
        error: action.error,
      });

    case FETCH_CONFIG_LOADING:
      return state.set("loading", action.loading);

    case FETCH_CONFIG_SUCCESS: {
      const responseData = JSON.parse(action.config.responseData);
      if (!responseData) return state;

      const formattedSlots = {};
      if (responseData.Slots && Array.isArray(responseData.Slots)) {
        responseData.Slots.forEach((slot) => {
          if (slot.Day && Array.isArray(slot.TimeSlots)) {
            formattedSlots[slot.Day] = slot.TimeSlots.map((timeSlot) => ({
              startTime: timeSlot.StartTime,
              endTime: timeSlot.EndTime,
            }));
          }
        });
      }

      return state.withMutations((map) => {
        map.setIn(["currentRestaurantData", "slots"], fromJS(formattedSlots));
        map.setIn(
          ["currentRestaurantData", "settings", "useAppointmentSystem"],
          responseData.UseAppointmentSystem || false
        );
        map.setIn(
          ["currentRestaurantData", "settings", "preStartMinutes"],
          responseData.PreStartMinutes || 10
        );

        map.set("error", null);
        map.set("loading", false);
      });
    }

    case FETCH_CONFIG_ERROR:
      return state.merge({
        loading: false,
        error: action.error,
      });

    case CUSTOMER_ERROR:
      return state.merge({
        error: action.payload,
      });

    case CLEAR_ERROR:
      return state.set("error", null);

    default:
      return state;
  }
}

// Action Creators
export const setDateSlotVisible = (visible, data = null) => ({
  type: SET_DATE_SLOT_VISIBLE,
  visible,
  data,
});

export const setDateSlotData = (data) => ({
  type: SET_DATE_SLOT_DATA,
  data,
});

export const addTimeSlot = (day, startTime, endTime) => ({
  type: ADD_TIME_SLOT,
  day,
  startTime,
  endTime,
});

export const removeTimeSlot = (day, slotIndex) => ({
  type: REMOVE_TIME_SLOT,
  day,
  slotIndex,
});

export const updateSettings = (settings) => ({
  type: UPDATE_SETTINGS,
  settings,
});

export const saveRestaurantSlots = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: SAVE_SLOTS_LOADING, loading: true });

      const brandId = cookie.load("PaketYoldaId");
      const state = getState();
      const dateSlotSystem = state.get("dateSlotSystemReducer");
      const currentData = dateSlotSystem.get("currentRestaurantData");
      const dateSlotData = dateSlotSystem.get("dateSlotData");

      const slots = currentData.get("slots").toJS();
      const settings = currentData.get("settings").toJS();

      const allSlots = [];
      Object.entries(slots).forEach(([day, daySlots]) => {
        daySlots.forEach((slot) => {
          allSlots.push({
            day: parseInt(day),
            startTime: slot.startTime,
            endTime: slot.endTime,
          });
        });
      });

      const uniqueSlots = Array.from(
        new Set(
          allSlots.map((slot) =>
            JSON.stringify({
              day: slot.day,
              startTime: slot.startTime,
              endTime: slot.endTime,
            })
          )
        )
      ).map((slotStr) => JSON.parse(slotStr));

      const groupedSlots = uniqueSlots.reduce((acc, slot) => {
        if (!acc[slot.day]) {
          acc[slot.day] = [];
        }
        acc[slot.day].push({
          startTime: slot.startTime,
          endTime: slot.endTime,
        });
        return acc;
      }, {});

      const requestData = {
        brandId: brandId,
        restaurantId: dateSlotData.get("BrandId"),
        slots: Object.entries(groupedSlots).map(([day, timeSlots]) => ({
          day: parseInt(day),
          timeSlots: timeSlots,
        })),
        useAppointmentSystem: settings.useAppointmentSystem,
        preStartMinutes: settings.preStartMinutes,
      };

      const response = await axios({
        method: "POST",
        url: `${
          brand.urlSocketPaketYolda
        }api/Appointment/saveAppointmentConfig`,
        headers: {
          Authorization: "Basic " + base64.encode("JarvisPos:Shark123*"),
        },
        data: requestData,
      });

      if (response.data.isStatus) {
        dispatch({ type: SAVE_RESTAURANT_SLOTS, brandId });
        dispatch({ type: SAVE_SLOTS_SUCCESS });
        dispatch({
          type: CUSTOMER_ERROR,
          payload: "Randevu ayarları başarıyla kaydedildi.",
        });
        // Clear error after 2 seconds
        setTimeout(() => {
          dispatch({ type: CLEAR_ERROR });
        }, 2000);
      } else {
        throw new Error(
          response.data.message || "Kaydetme işlemi başarısız oldu"
        );
      }
    } catch (error) {
      dispatch({ type: SAVE_SLOTS_ERROR, error: error.message });
      dispatch({
        type: CUSTOMER_ERROR,
        payload:
          error.message || "Randevu ayarları kaydedilirken bir hata oluştu.",
      });
    }
  };
};

export const fetchAppointmentConfig = () => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: FETCH_CONFIG_LOADING, loading: true });

      const brandId = cookie.load("PaketYoldaId");
      const state = getState();
      const dateSlotSystem = state.get("dateSlotSystemReducer");
      const dateSlotData = dateSlotSystem.get("dateSlotData");
      const restaurantId = dateSlotData.get("BrandId");

      const response = await axios({
        method: "GET",
        url: `${brand.urlSocketPaketYolda}api/Appointment/getAppointmentConfig`,
        headers: {
          Authorization: "Basic " + base64.encode("JarvisPos:Shark123*"),
        },
        params: {
          brandId,
          restaurantId,
        },
      });

      if (response.data.isStatus) {
        const emptyConfig = {
          isStatus: true,
          responseData: JSON.stringify({
            BrandId: brandId,
            RestaurantId: restaurantId,
            Slots: [],
            UseAppointmentSystem: false,
            PreStartMinutes: 10,
          }),
        };

        dispatch({
          type: FETCH_CONFIG_SUCCESS,
          config: response.data.responseData ? response.data : emptyConfig,
        });
      } else {
        if (response.data.warningCode !== 0) {
          throw new Error(response.data.message || "Konfigurasyon yüklenemedi");
        }

        dispatch({
          type: FETCH_CONFIG_SUCCESS,
          config: {
            isStatus: true,
            responseData: JSON.stringify({
              BrandId: brandId,
              RestaurantId: restaurantId,
              Slots: [],
              UseAppointmentSystem: false,
              PreStartMinutes: 10,
            }),
          },
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_CONFIG_SUCCESS,
        config: {
          isStatus: true,
          responseData: JSON.stringify({
            BrandId: brandId,
            RestaurantId: restaurantId,
            Slots: [],
            UseAppointmentSystem: false,
            PreStartMinutes: 10,
          }),
        },
      });
    } finally {
      dispatch({ type: FETCH_CONFIG_LOADING, loading: false });
    }
  };
};
